import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { GenerateStoriesModal } from "./GenerateStoriesModal";
import { PlusIcon } from "@radix-ui/react-icons";
import { STORYBOARD_TYPES } from "@/utils/constants";
import {
  useMultiselectedNavTreeNode,
  useSelectedNavTreeNode,
} from "@/components/Canvas/stores/canvasStore";
import { useIsChatLoading } from "@/store/zustand/aiContextStore";

interface StoryGenerationManagerProps {
  canvasComponent?: {
    componentName?: string;
    filename?: string;
  };
  buttonStyle?: React.CSSProperties;
  buttonSize?: {
    width: number;
    height: number;
  };
  fontSize?: number;
  className?: string;
  hasExistingStories?: boolean;
}

interface StoryGenerationUIProps {
  storyboardsToDraw: any[];
  canvasComponentKey?: string;
  maxZIndex: number;
  canvasComponent?: {
    componentName?: string;
    filename?: string;
  };
}

const DEFAULT_STORY_GENERATION_ITEM = {
  x: -100,
  y: -50,
  width: 200,
  height: 100,
};

/**
 * Handles all story generation UI logic including positioning and conditional rendering
 */
export const StoryGenerationUI: React.FC<StoryGenerationUIProps> = ({
  storyboardsToDraw,
  canvasComponentKey,
  maxZIndex,
  canvasComponent,
}) => {
  const { storyboardIdSelected } = useSelectedNavTreeNode();
  const { multiselectedElements } = useMultiselectedNavTreeNode();

  const storyStoryboards = useMemo(
    () =>
      storyboardsToDraw.filter(
        (sb) => sb.type === STORYBOARD_TYPES.STORY && !sb.deleted,
      ),
    [storyboardsToDraw],
  );

  const selectedStoryboard = storyboardsToDraw.find(
    (sb) => storyboardIdSelected === sb.id,
  );
  const selectedStory =
    selectedStoryboard?.type === STORYBOARD_TYPES.STORY &&
    (multiselectedElements?.length || 0) <= 1;

  const lastStoryStoryboard = storyStoryboards[storyStoryboards.length - 1];

  let x, y, width, height;
  if (selectedStoryboard && lastStoryStoryboard) {
    x = lastStoryStoryboard.x + lastStoryStoryboard.width + 100;
    y = lastStoryStoryboard.y;
    width = selectedStoryboard.width;
    height = selectedStoryboard.height;
  } else {
    x = DEFAULT_STORY_GENERATION_ITEM.x;
    y = DEFAULT_STORY_GENERATION_ITEM.y;
    width = DEFAULT_STORY_GENERATION_ITEM.width;
    height = DEFAULT_STORY_GENERATION_ITEM.height;
  }

  // Hide Generate stories button in subcanvas when stories exist but no story is selected
  const hideGenerateStoriesInSubcanvas =
    canvasComponentKey && storyStoryboards.length !== 0 && !selectedStory;

  // Calculate button position and size - at the end of stories or fallback position
  let buttonX, buttonY, buttonWidth, buttonHeight, fontSize;
  if (storyStoryboards.length > 0) {
    // Position at the end of all stories
    const lastStory = storyStoryboards[storyStoryboards.length - 1];
    buttonX = lastStory.x + lastStory.width + 100; // 100px to the right of the last story

    // Make button size proportional to the story size, with reasonable bounds
    const storyHeight = lastStory.height || 100;
    const proportionalHeight = storyHeight * 0.8;

    // Set minimum and maximum bounds that make sense
    buttonHeight = Math.max(Math.min(proportionalHeight, 400), 60); // Min 60px, Max 400px
    buttonWidth = buttonHeight; // Square button
    buttonY = lastStory.y + (storyHeight - buttonHeight) / 2; // Center the button with the last story

    // Scale font size more proportionally - no artificial minimum for small buttons
    fontSize = Math.max(buttonHeight / 8, 10); // Minimum 10px font size, larger ratio for better readability
  } else {
    // Fallback position when no stories exist
    buttonX = x;
    buttonY = y - 50;
    buttonWidth = 120;
    buttonHeight = 120;
    fontSize = 16;
  }

  // Return early only if we should hide everything
  if (hideGenerateStoriesInSubcanvas) {
    return (
      <StoryGenerationManager
        canvasComponent={canvasComponent}
        buttonStyle={{
          position: "absolute",
          left: buttonX,
          top: buttonY,
          transformOrigin: "top left",
          zIndex: maxZIndex + 20,
        }}
        buttonSize={{
          width: buttonWidth,
          height: buttonHeight,
        }}
        fontSize={fontSize}
        className="absolute"
      />
    );
  }

  return (
    <>
      {storyStoryboards.length === 0 && canvasComponentKey ? (
        // When no stories exist, show text message and Generate Stories button
        <div
          className="absolute flex flex-col items-center gap-4"
          style={{
            position: "absolute",
            left: x - 240,
            top: y,
            transformOrigin: "top left",
            zIndex: maxZIndex + 20,
            width: "480px",
          }}
        >
          <StoryGenerationManager
            canvasComponent={canvasComponent}
            hasExistingStories={false}
          />
        </div>
      ) : storyStoryboards.length > 0 ? (
        // When stories exist, show the button
        <StoryGenerationManager
          canvasComponent={canvasComponent}
          buttonStyle={{
            position: "absolute",
            left: buttonX,
            top: buttonY,
            transformOrigin: "top left",
            zIndex: maxZIndex + 20,
          }}
          buttonSize={{
            width: buttonWidth,
            height: buttonHeight,
          }}
          fontSize={fontSize}
          className="absolute rounded-lg"
        />
      ) : null}
    </>
  );
};

/**
 * Manages story generation modal state and renders the "Generate stories" button
 */
export const StoryGenerationManager: React.FC<StoryGenerationManagerProps> = ({
  canvasComponent,
  buttonStyle,
  buttonSize,
  fontSize,
  className,
  hasExistingStories = true,
}) => {
  const [isGenerateStoriesModalOpen, setIsGenerateStoriesModalOpen] =
    useState(false);
  const isChatLoading = useIsChatLoading();

  const handleGenerateStoriesClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!isChatLoading) {
        setIsGenerateStoriesModalOpen(true);
      }
    },
    [isChatLoading],
  );

  return (
    <>
      {/* Generate Stories Button */}
      <Button
        variant={hasExistingStories ? "secondary" : "primary"}
        size="lg"
        className={`flex items-center justify-center gap-1 rounded-lg ${
          hasExistingStories ? "border-dashed" : ""
        } ${className || ""}`}
        style={{
          ...buttonStyle,
          ...(buttonSize && {
            width: buttonSize.width,
            height: buttonSize.height,
          }),
          ...(fontSize && {
            fontSize: `${fontSize}px`,
          }),
        }}
        onMouseDown={handleGenerateStoriesClick}
        disabled={isChatLoading}
      >
        {hasExistingStories && (
          <PlusIcon
            style={{
              width: `${Math.max((fontSize || 16) * 1.2, 12)}px`,
              height: `${Math.max((fontSize || 16) * 1.2, 12)}px`,
              color: "currentColor",
              flexShrink: 0,
            }}
          />
        )}
        <span>{hasExistingStories ? "Story" : "Generate stories"}</span>
      </Button>

      {/* Note: hasExistingStories is always true since this option is only shown when stories exist.
       We need to keep using this Prop since StoryGeneration can be triggered from CanvasContextMenu. */}
      <GenerateStoriesModal
        isOpen={isGenerateStoriesModalOpen}
        onClose={() => setIsGenerateStoriesModalOpen(false)}
        componentName={canvasComponent?.componentName}
        componentPath={canvasComponent?.filename}
        hasExistingStories={hasExistingStories}
      />
    </>
  );
};
